import { defineMessages, IntlShape } from 'react-intl';

/**
 * Shared defineMessages map for translating unit type nouns (e.g. "guest", "adult", "child").
 *
 * Used across booking_request, checkout, and summary components when
 * translating dynamic unit type names from the API or D13nStore.
 *
 * Keys are lowercase to allow case-insensitive lookup via `translateUnitNoun()`.
 */
export const unitNounMessages = defineMessages({
  guest: { defaultMessage: 'Guest' },
  guests: { defaultMessage: 'Guests' },
  adult: { defaultMessage: 'Adult' },
  adults: { defaultMessage: 'Adults' },
  child: { defaultMessage: 'Child' },
  children: { defaultMessage: 'Children' },
  senior: { defaultMessage: 'Senior' },
  seniors: { defaultMessage: 'Seniors' },
  person: { defaultMessage: 'Person' },
  people: { defaultMessage: 'People' },
  youth: { defaultMessage: 'Youth' },
  infant: { defaultMessage: 'Infant' },
  infants: { defaultMessage: 'Infants' },
  student: { defaultMessage: 'Student' },
  students: { defaultMessage: 'Students' },
  minor: { defaultMessage: 'Minor' },
  minors: { defaultMessage: 'Minors' },
  ticket: { defaultMessage: 'Ticket' },
  tickets: { defaultMessage: 'Tickets' },
});

interface TranslateOptions {
  lowercase?: boolean;
}

/**
 * Translate a unit noun using the shared defineMessages map.
 * Falls back to the raw value if no matching message is found.
 */
export const translateUnitNoun = (
  intl: IntlShape,
  value: string | null | undefined,
  options?: TranslateOptions
): string => {
  const key = value?.toLowerCase();
  if (key && key in unitNounMessages) {
    const result = intl.formatMessage(
      unitNounMessages[key as keyof typeof unitNounMessages]
    );
    return options?.lowercase ? result.toLowerCase() : result;
  }
  return value ?? '';
};

/**
 * Get the translated label for a unit type, choosing between singular and plural forms.
 */
export const getUnitTypeLabel = (
  intl: IntlShape,
  singular: string,
  plural: string,
  isPlural: boolean,
  options?: TranslateOptions
): string => {
  const value = isPlural ? plural : singular;
  return translateUnitNoun(intl, value, options);
};
